import type { SupportedLocale } from './detect'

const NUM: Record<string, Intl.NumberFormat> = {}
const DATE: Record<string, Intl.DateTimeFormat> = {}

function nf(locale: string, opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(opts)}`
  return (NUM[key] ??= new Intl.NumberFormat(locale, opts))
}

function df(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(opts)}`
  return (DATE[key] ??= new Intl.DateTimeFormat(locale, opts))
}

export function formatNumber(value: number, locale: SupportedLocale, opts: Intl.NumberFormatOptions = {}): string {
  return nf(locale, opts).format(value)
}

export function formatPercent(value: number, locale: SupportedLocale): string {
  return nf(locale, { style: 'percent', maximumFractionDigits: 0 }).format(value)
}

/** Moeda é independente do idioma — default BRL em qualquer locale. */
export function formatCurrency(cents: number, locale: SupportedLocale, currency = 'BRL'): string {
  return nf(locale, { style: 'currency', currency }).format(cents / 100)
}

export function formatDate(value: string | number | Date, locale: SupportedLocale): string {
  return df(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
}

export function formatDateTime(value: string | number | Date, locale: SupportedLocale): string {
  return df(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value))
}

export function formatRelative(from: string | number | Date, locale: SupportedLocale, now = Date.now()): string {
  const diffMs = new Date(from).getTime() - now
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const abs = Math.abs(diffMs)
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000], ['month', 2_592_000_000], ['day', 86_400_000],
    ['hour', 3_600_000], ['minute', 60_000],
  ]
  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.trunc(diffMs / ms), unit)
  }
  return rtf.format(Math.trunc(diffMs / 1000), 'second')
}
